/**
 * Validación de inventario del proyecto — pestaña "Materiales" (READ-ONLY).
 *
 * Responde la pregunta con la que arranca todo abastecimiento y que hasta ahora
 * sólo tenía el agente: **¿qué necesito, qué tengo, DÓNDE está y qué me falta?**
 *
 * `check_materials_availability` existía únicamente en el carril MCP (16
 * archivos, CERO bajo `src/`). Lo que la web sí tenía —el cálculo
 * retirar-vs-comprar de `materialRequestService`— vive río abajo: corre DENTRO
 * de la aprobación de un `material_request` que ya existe y CON efectos (consumo
 * FIFO + orden de compra draft). Esta pantalla es el paso ANTERIOR y sin
 * efectos: mirar antes de pedir.
 *
 * Tres decisiones que conviene no revertir sin leer por qué:
 *
 * 1. **La lista es editable.** Sale de `quotation_items` de la cotización
 *    aprobada (la única fuente que ya existe en la DB con cantidades por
 *    proyecto), pero es un punto de partida, no un contrato: la obra cambia y
 *    el comprador necesita poder agregar, quitar y ajustar antes de consultar.
 *    Sin cotización aprobada la pantalla NO se bloquea: arranca vacía.
 *
 * 2. **El cálculo no está acá.** Ni el match contra el catálogo, ni el rollup
 *    por bodega, ni el faltante. Todo eso lo hace el servidor
 *    (`reconcileMaterialsTools.checkMaterialsAvailability`) y esta pantalla lo
 *    consume vía `materialsAvailabilityService`. Una segunda copia en el
 *    frontend divergiría del agente al primer cambio — es exactamente la deriva
 *    de #353/#454 que documenta CLAUDE.md.
 *
 * 3. **No hay ninguna acción que escriba.** El faltante NO se convierte en
 *    orden de compra desde acá: eso es `create_purchase_from_shortfall`, que es
 *    admin-only y ESCRIBE. Mezclar la consulta con la acción convierte "mirar"
 *    en "ya pediste".
 *
 * Mobile-first: Jorge abre esto en obra. Controles de 44px, cuerpo ≥14px, todo
 * apilado bajo 640px (`ProjectMaterialsTab.css`).
 *
 * @module components/projects/ProjectMaterialsTab
 */

import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import {
  getProjectMaterialList,
  checkMaterialsAvailability,
} from '../../services/materialsAvailabilityService';
import './ProjectMaterialsTab.css';

/** Centinela que devuelve el servidor cuando nadie dijo la unidad (#356). */
const UNIT_UNSPECIFIED = '(sin especificar)';

let rowSeq = 0;
const newRow = (values = {}) => ({
  key: `row-${(rowSeq += 1)}`,
  name: '',
  quantity: '',
  unit: '',
  alcance: '',
  ...values,
});

const plural = (n, singular, pluralWord) => (n === 1 ? singular : pluralWord);

/**
 * Frase de resumen. NO es un volcado de los contadores que devuelve la tool:
 * lo que el comprador necesita saber es cuántos ítems tiene que comprar, y los
 * avisos (sin coincidencia / sin unidad) sólo aparecen cuando aplican.
 */
const summarySentence = (summary) => {
  const {
    total_items: total = 0,
    items_complete_in_stock: complete = 0,
    items_with_shortfall: shortfall = 0,
    items_unmatched: unmatched = 0,
    items_unit_unspecified: unspecified = 0,
  } = summary || {};

  const parts = [];
  if (shortfall > 0) {
    parts.push(`${shortfall} ${plural(shortfall, 'ítem', 'ítems')} por comprar`);
    parts.push(`${complete} ${plural(complete, 'cubierto', 'cubiertos')} en bodega`);
  } else {
    parts.push(`Nada por comprar: los ${total} ${plural(total, 'ítem está cubierto', 'ítems están cubiertos')} en bodega`);
  }
  if (unmatched > 0) {
    parts.push(`${unmatched} sin coincidencia en el catálogo`);
  }
  if (unspecified > 0) {
    parts.push(`${unspecified} sin unidad — preguntala antes de comprar`);
  }
  return parts.join(' · ');
};

/**
 * Aviso legible del estado de coincidencia. El valor crudo (`matched` / `fuzzy`
 * / `unmatched`) no se muestra: no le dice nada a quien compra.
 */
const matchNotice = (status) => {
  if (status === 'unmatched') return 'No está en el catálogo — se cuenta entero como faltante';
  if (status === 'fuzzy') return 'Coincidencia dudosa — verificá que sea el mismo producto';
  return null;
};

const ProjectMaterialsTab = ({ projectId, active = false }) => {
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const seededRef = useRef(null);

  const { data: listPayload, isLoading } = useQuery({
    queryKey: ['projectMaterialList', projectId],
    // Sólo al abrir la pestaña: la validación de inventario es una consulta
    // deliberada, no algo que deba dispararse por entrar al proyecto.
    enabled: !!projectId && active,
    queryFn: () => getProjectMaterialList(projectId),
  });

  const listError = listPayload?.error || null;
  const quotation = listPayload?.data?.quotation || null;
  const excluded = listPayload?.data?.excluded || [];

  // Siembra UNA vez por proyecto. Re-sembrar en cada render del query pisaría lo
  // que el usuario ya editó (y esa edición es el punto de la pantalla).
  useEffect(() => {
    const payload = listPayload?.data;
    if (!payload || seededRef.current === projectId) return;
    seededRef.current = projectId;
    setRows((payload.items || []).map((item) => newRow(item)));
  }, [listPayload, projectId]);

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, newRow()]);
  const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

  const handleCheck = async () => {
    setChecking(true);
    setCheckError(null);
    const { data, error } = await checkMaterialsAvailability({
      projectId,
      items: rows.map((r) => ({
        name: r.name,
        quantity: Number(r.quantity),
        unit: r.unit,
        alcance: r.alcance,
      })),
    });
    if (error) {
      setCheckError(error);
      setResult(null);
    } else {
      setResult(data);
    }
    setChecking(false);
  };

  const hasUsableRow = rows.some((r) => (r.name || '').trim() && Number(r.quantity) > 0);

  return (
    <div data-testid="project-materials-tab" className="project-materials-tab">
      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-1">
            <i className="bi bi-box-seam me-2"></i>
            Validación de inventario
          </h5>
          <div data-testid="materials-readonly-note" className="materials-note">
            Consulta informativa: no consume inventario ni crea órdenes de compra.
          </div>
        </Card.Header>

        <Card.Body>
          {isLoading && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <Spinner animation="border" size="sm" />
              <span>Cargando la lista de la cotización…</span>
            </div>
          )}

          {listError && (
            <Alert variant="warning">
              No se pudo leer la cotización del proyecto. Podés cargar los materiales a mano.
            </Alert>
          )}

          {quotation && (
            <div className="materials-source mb-3">
              Lista tomada de la cotización aprobada <strong>{quotation.quotation_number}</strong>.
              Ajustala si la obra cambió: lo que consultás es lo que ves acá.
            </div>
          )}

          {excluded.length > 0 && (
            <div data-testid="materials-excluded-note" className="materials-note mb-3">
              De la cotización {plural(excluded.length, 'quedó fuera', 'quedaron fuera')}{' '}
              {excluded.length} {plural(excluded.length, 'línea', 'líneas')} que no
              {plural(excluded.length, ' es', ' son')} material:{' '}
              {excluded.map((e) => `${e.name} (${e.label})`).join(', ')}. Si igual hay que
              conseguirlas, agregalas a mano.
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div data-testid="materials-empty-list" className="materials-empty">
              {quotation
                ? 'La cotización aprobada no tiene líneas de material.'
                : 'Este proyecto no tiene cotización aprobada todavía.'}{' '}
              Agregá los materiales a mano para consultar qué hay en bodega.
            </div>
          )}

          {rows.length > 0 && (
            <div className="materials-rows">
              {rows.map((row) => (
                <div key={row.key} data-testid="material-row" className="materials-row">
                  <Form.Group className="materials-field materials-field-name">
                    <span className="materials-field-label" aria-hidden="true">Material</span>
                    <Form.Control
                      className="materials-tap-target"
                      type="text"
                      value={row.name}
                      aria-label={`Material${row.name ? `: ${row.name}` : ''}`}
                      placeholder="Nombre del material"
                      onChange={(e) => updateRow(row.key, 'name', e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="materials-field materials-field-qty">
                    <span className="materials-field-label" aria-hidden="true">Cantidad</span>
                    <Form.Control
                      className="materials-tap-target"
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={row.quantity}
                      aria-label={`Cantidad${row.name ? ` de ${row.name}` : ''}`}
                      onChange={(e) => updateRow(row.key, 'quantity', e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="materials-field materials-field-unit">
                    <span className="materials-field-label" aria-hidden="true">Unidad</span>
                    <Form.Control
                      className="materials-tap-target"
                      type="text"
                      value={row.unit}
                      aria-label={`Unidad${row.name ? ` de ${row.name}` : ''}`}
                      // #356: vacío se OMITE al consultar; el servidor la resuelve
                      // desde el catálogo. No se inventa "unidad".
                      placeholder="la del catálogo"
                      onChange={(e) => updateRow(row.key, 'unit', e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="materials-field materials-field-alcance">
                    <span className="materials-field-label" aria-hidden="true">Alcance</span>
                    <Form.Control
                      className="materials-tap-target"
                      type="text"
                      value={row.alcance}
                      aria-label={`Alcance${row.name ? ` de ${row.name}` : ''}`}
                      placeholder="opcional"
                      onChange={(e) => updateRow(row.key, 'alcance', e.target.value)}
                    />
                  </Form.Group>

                  <div className="materials-field materials-field-action">
                    <Button
                      variant="outline-danger"
                      className="materials-tap-target"
                      aria-label={`Quitar${row.name ? ` ${row.name}` : ' ítem'}`}
                      onClick={() => removeRow(row.key)}
                    >
                      <i className="bi bi-trash" aria-hidden="true"></i>
                      <span className="materials-action-text ms-1">Quitar</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="materials-actions mt-3">
            <Button
              variant="outline-secondary"
              className="materials-tap-target"
              onClick={addRow}
            >
              <i className="bi bi-plus-circle me-1" aria-hidden="true"></i>
              Agregar ítem
            </Button>
            <Button
              variant="primary"
              className="materials-tap-target"
              disabled={!hasUsableRow || checking}
              onClick={handleCheck}
            >
              {checking ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Consultando…
                </>
              ) : (
                <>
                  <i className="bi bi-search me-1" aria-hidden="true"></i>
                  Consultar disponibilidad
                </>
              )}
            </Button>
          </div>

          {checkError && (
            <Alert variant="danger" className="mt-3">
              {checkError.message || 'No se pudo consultar la disponibilidad.'}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {result && (
        <div data-testid="materials-availability-result" className="materials-result">
          <div data-testid="availability-summary" className="materials-result-summary">
            {summarySentence(result.summary)}
          </div>

          {(result.groups || []).map((group) => (
            <section
              key={group.alcance}
              data-testid={`availability-group-${group.alcance}`}
              className="materials-result-group"
            >
              <h6 className="materials-result-group-title">{group.alcance}</h6>

              {(group.items || []).map((item) => {
                const notice = matchNotice(item.match_status);
                const unitKnown = item.unit && item.unit !== UNIT_UNSPECIFIED;
                const unitLabel = unitKnown ? ` ${item.unit}` : '';
                const short = item.qty_to_purchase > 0;
                return (
                  <article
                    key={item.name}
                    data-testid={`availability-row-${item.name}`}
                    className={`materials-result-item${short ? ' is-short' : ''}`}
                  >
                    <header className="materials-result-item-head">
                      <span className="materials-result-item-name">{item.name}</span>
                      {short ? (
                        <span className="materials-flag materials-flag-short">Falta</span>
                      ) : (
                        <span className="materials-flag materials-flag-ok">Completo</span>
                      )}
                    </header>

                    <dl className="materials-result-stats">
                      <div className="materials-stat">
                        <dt>Necesario</dt>
                        <dd data-testid="qty-needed">{item.qty_needed}{unitLabel}</dd>
                      </div>
                      <div className="materials-stat">
                        <dt>En stock</dt>
                        <dd data-testid="qty-in-stock">{item.qty_in_stock}{unitLabel}</dd>
                      </div>
                      <div className="materials-stat materials-stat-wide">
                        <dt>Dónde está</dt>
                        <dd data-testid="location">{item.location}</dd>
                      </div>
                      <div className="materials-stat">
                        <dt>A comprar</dt>
                        <dd data-testid="qty-to-purchase">{item.qty_to_purchase}{unitLabel}</dd>
                      </div>
                    </dl>

                    {!unitKnown && (
                      <p className="materials-result-warning">
                        Sin unidad: nadie la dijo y el catálogo tampoco la tiene. Preguntala
                        antes de comprar.
                      </p>
                    )}
                    {notice && <p className="materials-result-warning">{notice}</p>}
                  </article>
                );
              })}
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectMaterialsTab;
